/**
 * Team, External certificates (plan docs/plans/lms-plan.md 7.5, 13 tab 6;
 * Appendix A GET /admin/external-certs and POST
 * /admin/external-certs/:id/review; 18.15, 18.21, 18.22; R17, R20; WP-45).
 *
 * Replaces WP-03's placeholder in place: tabs/team/subviews.ts imports this
 * file's DEFAULT export (no props), mounted at
 * /lms/team/external-certificates/* (18.3).
 *
 * Certificates people earned elsewhere (CITI GCP, CPR/BLS, an IATA course)
 * and uploaded, from holders the admin reaches at any of the admin's sites
 * (18.21), newest first. Pending ones are reviewed here (ReviewDialog: view
 * the file, accept or reject with a note, sign with the password); accepted
 * and rejected ones are the history. Nobody reviews their own upload (the
 * server refuses it too, 18.15).
 */
import { useState } from 'react';
import { ExternalLink, FileBadge } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import Modal from '../../../../ui/Modal';
import { chipCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { useAuth } from '../../../../contexts/AuthContext';
import { listExternalCertQueue } from '../../../api/team';
import type { AdminExternalCert, ExternalCertQueueStatus, ReviewDecision, ReviewExternalCertResult } from '../../../api/team';
import { Pill } from '../../../components/StatusPill';
import type { PillTone } from '../../../components/StatusPill';
import {
  formatDay,
  linkBtnCls,
  metaCls,
  personName,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { LeaverTag, LoadFailed, Loading, useAsync } from '../matrix/shared';
import { ReviewDialog, useOpenCertFile } from './ReviewDialog';

const STATUSES: Array<{ value: ExternalCertQueueStatus; label: string }> = [
  { value: 'pending', label: 'Waiting for review' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const STATE: Record<ExternalCertQueueStatus, { word: string; tone: PillTone }> = {
  pending: { word: 'Pending review', tone: 'amber' },
  accepted: { word: 'Accepted', tone: 'green' },
  rejected: { word: 'Rejected', tone: 'red' },
};

/** Rows per page. */
export const EXTERNAL_PAGE = 50;

export function ExternalCertsView() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [status, setStatus] = useState<ExternalCertQueueStatus>('pending');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<AdminExternalCert | null>(null);
  const file = useOpenCertFile();
  const queue = useAsync(
    () => listExternalCertQueue({ status, limit: EXTERNAL_PAGE, offset }),
    `${status}:${offset}`,
    'The external certificates could not be loaded.'
  );
  const items = queue.data?.items ?? [];
  const page = queue.data?.page;

  const done = (result: ReviewExternalCertResult, decision: ReviewDecision) => {
    const who = open?.holder ? personName(open.holder) : 'the holder';
    const title = open?.title ?? 'The certificate';
    setOpen(null);
    if (decision === 'rejected') toast(`${title} was rejected; ${who} is told why.`, { type: 'success' });
    else if (result.completion) toast(`${title} was accepted and counts toward the requirement for ${who}.`, { type: 'success' });
    else toast(`${title} was accepted for ${who}.`, { type: 'success' });
    queue.reload();
  };

  return (
    <div className="space-y-4" data-testid="lms-external-certs">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Review state">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={chipCls(status === s.value)}
            aria-pressed={status === s.value}
            onClick={() => {
              setStatus(s.value);
              setOffset(0);
            }}
            data-testid={`external-status-${s.value}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {file.blockedUrl ? (
        <p className={metaCls}>
          {'Your browser blocked the new tab. '}
          <a href={file.blockedUrl} target="_blank" rel="noopener noreferrer" className={linkBtnCls}>
            Open the file
          </a>
        </p>
      ) : null}
      {queue.error ? <LoadFailed message={queue.error} onRetry={queue.reload} testId="external-error" /> : null}
      {!queue.data && queue.loading ? <Loading rows={3} /> : null}
      {queue.data && items.length === 0 ? (
        <EmptyState
          icon={FileBadge}
          variant="card"
          title={status === 'pending' ? 'Nothing is waiting for review' : 'Nothing here yet'}
          hint={status === 'pending' ? 'Certificates people upload on their Records tab appear here for review.' : undefined}
        />
      ) : null}

      {items.length > 0 ? (
        <div className={tableCardCls}>
          <table className={tableCls}>
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={thCls}>
                  Holder
                </th>
                <th scope="col" className={thCls}>
                  Certificate
                </th>
                <th scope="col" className={thCls}>
                  Issued
                </th>
                <th scope="col" className={thCls}>
                  Expires
                </th>
                <th scope="col" className={thCls}>
                  Offered against
                </th>
                <th scope="col" className={thCls}>
                  State
                </th>
                <th scope="col" className={thCls}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {items.map((c) => {
                const own = Boolean(user?.id) && (c.user_id === user?.id || c.uploaded_by === user?.id);
                const state = STATE[c.status] ?? { word: c.status, tone: 'gray' as PillTone };
                return (
                  <tr key={c.id} data-testid={`external-row-${c.id}`}>
                    <td className={tdCls}>
                      <div className="font-medium">
                        {c.holder ? personName(c.holder) : 'Unknown person'}
                        <LeaverTag deleted={c.holder?.deleted} />
                      </div>
                      {c.holder?.email && c.holder.full_name ? <div className={metaCls}>{c.holder.email}</div> : null}
                    </td>
                    <td className={tdCls}>
                      <div className="font-medium">{c.title}</div>
                      <div className={metaCls}>{c.issuer}</div>
                      {c.credential_id ? <div className={metaCls}>{`Credential ${c.credential_id}`}</div> : null}
                    </td>
                    <td className={tdCls}>{formatDay(c.issued_on)}</td>
                    <td className={tdCls}>{c.expires_on ? formatDay(c.expires_on) : 'Does not expire'}</td>
                    <td className={tdCls}>{c.course ? c.course.title : 'None named'}</td>
                    <td className={tdCls}>
                      <Pill tone={state.tone}>{state.word}</Pill>
                      {c.review_note ? <div className={metaCls}>{c.review_note}</div> : null}
                    </td>
                    <td className={tdCls}>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className={outlineBtnCls()} onClick={() => void file.open(c.id)} data-testid={`external-view-${c.id}`}>
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          View
                        </button>
                        {c.status === 'pending' ? (
                          own ? (
                            <span className={metaCls} data-testid={`external-own-${c.id}`}>
                              Another admin reviews your own certificate
                            </span>
                          ) : (
                            <button type="button" className={primaryBtnCls} onClick={() => setOpen(c)} data-testid={`external-review-${c.id}`}>
                              Review
                            </button>
                          )
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && (page.offset > 0 || page.hasMore) ? (
        <div className="flex justify-end gap-2">
          <button type="button" className={secondaryBtnCls} disabled={page.offset === 0 || queue.loading} onClick={() => setOffset(Math.max(0, page.offset - EXTERNAL_PAGE))}>
            Previous
          </button>
          <button type="button" className={secondaryBtnCls} disabled={!page.hasMore || queue.loading} onClick={() => setOffset(page.offset + EXTERNAL_PAGE)}>
            Next
          </button>
        </div>
      ) : null}

      <Modal isOpen={open !== null} onClose={() => setOpen(null)} title={open ? `Review: ${open.title}` : 'Review a certificate'}>
        {open ? <ReviewDialog cert={open} onCancel={() => setOpen(null)} onDone={done} /> : null}
      </Modal>
    </div>
  );
}

export default ExternalCertsView;
